import { useState, useEffect } from 'react';
import type { Business } from '../types';

interface GoogleSheetsExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void;
  businesses: Business[];
}

export interface ExportOptions {
  mode: 'create' | 'append';
  title?: string;
  spreadsheetId?: string;
  sheetName?: string;
  selectedColumns: string[];
}

export function GoogleSheetsExportModal({
  isOpen,
  onClose,
  onExport,
  businesses,
}: GoogleSheetsExportModalProps) {
  const [mode, setMode] = useState<'create' | 'append'>('create');
  const [title, setTitle] = useState(`Business Export ${new Date().toLocaleDateString()}`);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('Data');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());

  // Available columns from business data
  const availableColumns = businesses.length > 0 ? Object.keys(businesses[0]) : [];

  // Initialize with all columns selected
  useEffect(() => {
    if (isOpen && availableColumns.length > 0 && selectedColumns.size === 0) {
      setSelectedColumns(new Set(availableColumns));
    }
  }, [isOpen, availableColumns]);

  const toggleColumn = (column: string) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedColumns(new Set(availableColumns));
  };

  const deselectAll = () => {
    setSelectedColumns(new Set());
  };

  const handleExport = () => {
    const options: ExportOptions = {
      mode,
      selectedColumns: Array.from(selectedColumns),
    };

    if (mode === 'create') {
      options.title = title;
    } else {
      options.spreadsheetId = spreadsheetId;
      options.sheetName = sheetName;
    }

    onExport(options);
  };

  if (!isOpen) return null;

  const isValid =
    selectedColumns.size > 0 &&
    (mode === 'create' ? title.trim() !== '' : spreadsheetId.trim() !== '' && sheetName.trim() !== '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Export to Google Sheets</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Export Mode */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Export Mode</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setMode('create')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === 'create'
                    ? 'border-blue-500 bg-blue-500/20 text-white'
                    : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Create New Sheet</div>
                <div className="text-xs mt-1 opacity-75">Create a new spreadsheet</div>
              </button>
              <button
                onClick={() => setMode('append')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === 'append'
                    ? 'border-blue-500 bg-blue-500/20 text-white'
                    : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Append to Existing</div>
                <div className="text-xs mt-1 opacity-75">Add to existing sheet</div>
              </button>
            </div>
          </div>

          {/* Create Mode Options */}
          {mode === 'create' && (
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-slate-300 mb-2">
                Spreadsheet Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="My Business Export"
              />
            </div>
          )}

          {/* Append Mode Options */}
          {mode === 'append' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="spreadsheetId" className="block text-sm font-medium text-slate-300 mb-2">
                  Spreadsheet ID
                </label>
                <input
                  type="text"
                  id="spreadsheetId"
                  value={spreadsheetId}
                  onChange={(e) => setSpreadsheetId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                />
                <p className="mt-1 text-xs text-slate-400">
                  Found in the spreadsheet URL: docs.google.com/spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
                </p>
              </div>
              <div>
                <label htmlFor="sheetName" className="block text-sm font-medium text-slate-300 mb-2">
                  Sheet Name
                </label>
                <input
                  type="text"
                  id="sheetName"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Data"
                />
                <p className="mt-1 text-xs text-slate-400">
                  The name of the sheet tab (usually "Data", "Sheet1", etc.)
                </p>
              </div>
            </div>
          )}

          {/* Column Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-300">Select Columns to Export</label>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Select All
                </button>
                <span className="text-slate-500">|</span>
                <button
                  onClick={deselectAll}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="bg-slate-700/50 rounded-lg border border-slate-600 p-4 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                {availableColumns.map((column) => (
                  <label
                    key={column}
                    className="flex items-center gap-2 cursor-pointer hover:bg-slate-700/50 px-2 py-1 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(column)}
                      onChange={() => toggleColumn(column)}
                      className="w-4 h-4 rounded border-slate-500 text-blue-600 focus:ring-blue-500 focus:ring-offset-slate-800"
                    />
                    <span className="text-sm text-slate-300">{column}</span>
                  </label>
                ))}
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              {selectedColumns.size} of {availableColumns.length} columns selected
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!isValid}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors"
          >
            Export {businesses.length} Businesses
          </button>
        </div>
      </div>
    </div>
  );
}
